import React from 'react'
import debounce from 'lodash.debounce'

import { checkE164 } from '../../utilities'
import { checkChannelValidation } from '../../lib/api'

export const TYPES = {
  SMS: 'sms_voice',
  EMAIL: 'email'
}
const REVERSE_TYPE = {
  [TYPES.SMS]: 'number',
  [TYPES.EMAIL]: 'email'
}

const getNames = conflicts => {
  const names = [...new Set(conflicts.map(c => c.user.name))]
  return ` ${names.join(', ')}`
}

const DEBOUNCE_DELAY = 1000
const LOADING_DELAY = 200

const useChannelConflictCheck = type => {
  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [doneCheck, setDoneCheck] = React.useState(false)
  const [checked, setChecked] = React.useState(false)
  const valid = React.useMemo(() => doneCheck && error === '', [
    doneCheck,
    error
  ])

  const checkConflict = React.useCallback(async () => {
    if (!value.length) {
      return false
    }

    let checkVal = value
    if (type === TYPES.SMS) {
      const [error, formatted] = checkE164(value)

      if (error) {
        setError(error)
        return false
      }

      checkVal = formatted
    }

    try {
      const conflicts = await checkChannelValidation(type, checkVal)
      if (conflicts.length === 0) {
        return true
      }

      setError(`This ${REVERSE_TYPE[type]} is in use by:${getNames(conflicts)}`)
      return false
    } catch (err) {
      console.error('Error checking channel validation', err)
      return true
    }
  }, [type, value])

  const performCheck = React.useCallback(async () => {
    if (value.length < 4) {
      return
    }

    setError('')
    setLoading(true)
    const allowed = await checkConflict()

    // Slight delay after finishing to let the spinner show
    setTimeout(() => setLoading(false), LOADING_DELAY)

    setDoneCheck(allowed)
    setChecked(true)
  }, [checkConflict, value])

  // Resets state on value change
  React.useEffect(() => {
    setDoneCheck(false)
    setChecked(false)
    setError('')
  }, [value])

  // Debounced confict check
  React.useEffect(() => {
    const func = debounce(performCheck, DEBOUNCE_DELAY)

    func()

    // Cancels queued invocations
    return func.cancel
  }, [performCheck])

  return { value, setValue, loading, valid, error, checked }
}

export default useChannelConflictCheck
